import { useEffect, useState } from "react";
import { MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, ts } from "@/lib/api";
import { Panel, Th, Td, btn, field } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";

const on = (v) => v === true || String(v).toLowerCase() === "true";

export const AlertsPanel = ({ cfg, setCfg }) => {
  const [log, setLog] = useState([]);
  const load = () => api.get("/alerts").then((r) => setLog(r.data));
  useEffect(() => {
    load();
  }, []);
  const test = (path = "/alerts/test") => async () => {
    try {
      const r = (await api.post(path)).data;
      r.status === "sent" ? toast.success(`Email sent to ${r.to}`) : toast.error(r.error || "Alert not sent");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Email alerts to main admin" testId="alerts-panel">
      <div className="space-y-4 p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex items-center gap-3 pt-5 text-sm text-slate-200">
            <Switch checked={on(cfg.alerts_enabled)} onCheckedChange={(v) => setCfg({ ...cfg, alerts_enabled: v })} data-testid="settings-alerts-enabled" /> Email alerts on
          </label>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Send alerts to</span>
            <Input value={cfg.alert_email ?? ""} onChange={(e) => setCfg({ ...cfg, alert_email: e.target.value })} className={field} data-testid="settings-alert-email" />
          </label>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">997 overdue after (hours)</span>
            <Input type="number" value={cfg.ack_overdue_hours ?? 24} onChange={(e) => setCfg({ ...cfg, ack_overdue_hours: e.target.value })} className={field} data-testid="settings-ack-hours" />
          </label>
          <div className="pt-5"><Button variant="ghost" onClick={test()} className={btn.ghost} data-testid="alerts-test-btn"><MailCheck className="mr-1.5 h-4 w-4" /> Send test alert</Button></div>
          <label className="flex items-center gap-3 text-sm text-slate-200">
            <Switch checked={on(cfg.daily_summary)} onCheckedChange={(v) => setCfg({ ...cfg, daily_summary: v })} data-testid="settings-daily-summary" />
            <span>Daily summary at 07:00 UK<span className="block text-xs text-slate-500">Only sent when something is outstanding.</span></span>
          </label>
          <div><Button variant="ghost" onClick={test("/alerts/summary")} className={btn.ghost} data-testid="alerts-summary-now-btn"><MailCheck className="mr-1.5 h-4 w-4" /> Send summary now</Button></div>
          <label className="flex items-center gap-3 text-sm text-slate-200">
            <Switch checked={on(cfg.weekly_report)} onCheckedChange={(v) => setCfg({ ...cfg, weekly_report: v })} data-testid="settings-weekly-report" />
            <span>Weekly report, Monday 08:00 UK<span className="block text-xs text-slate-500">Last week's volumes, 997 turnaround and short shipments by site.</span></span>
          </label>
          <div><Button variant="ghost" onClick={test("/alerts/weekly")} className={btn.ghost} data-testid="alerts-weekly-now-btn"><MailCheck className="mr-1.5 h-4 w-4" /> Send weekly report now</Button></div>
        </div>
        <p className="text-xs text-slate-500">Emails go out when a 997 is overdue (checked hourly), when Group O rejects a message in a 997, or when an AS2 send fails. Sender: "Euro Packaging EDI Hub"; replies go to pt@epgroup.co.uk.</p>
        {log.length > 0 && (
          <div className="overflow-x-auto rounded-sm border border-slate-800">
            <table className="w-full" data-testid="alerts-log">
              <thead><tr><Th>When</Th><Th>Subject</Th><Th>To</Th><Th>Status</Th></tr></thead>
              <tbody className="divide-y divide-slate-800/70">
                {log.slice(0, 8).map((a) => (
                  <tr key={a.id}><Td className="whitespace-nowrap font-mono text-slate-500">{ts(a.at)}</Td><Td>{a.subject}</Td><Td className="font-mono">{a.to}</Td><Td><StatusBadge status={a.status === "sent" ? "processed" : "error"} label={a.status} />{a.error && <span className="ml-2 text-rose-300">{a.error}</span>}</Td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Panel>
  );
};
